import type { IAnalyticsEventFormField } from '@sravni/cosago-react-library/lib/types';
import type { FieldValues } from '@sravni/react-silent-authentication-form';
import { type FC, useCallback, useEffect } from 'react';

import { isSatisfyOption, useAppDispatch, useAppSelector } from 'shared/lib';
import {
  sendEventChangeFormFieldDefaultParam,
  sendEventFillFormField,
  sendEventShowStep,
  SendEventWrapper,
} from 'shared/lib/sendGAEvents';
import { useUpdateStateBySelector } from 'shared/lib/useUpdateStateBySelector';

import { isLoggedSelector } from 'entities/user';
import {
  ContactsFormValidationSchema,
  type TContactsFormFields,
  selectContactsInfoData,
  selectContactsInfoAuthData,
  setContactsInfo,
} from 'src/client/entities/creditForm';

import { submitContactsForm } from 'features/UpdateContactsInfo/lib/submitContactsForm';

import { FormFieldsControls } from '../../ContactsInfo.config';
import { ContactsInfoTexts } from '../../ContactsInfo.texts';
import type { AuthFormProps } from '../AuthForm';
import { AuthFormHoc } from '../AuthForm';
import { ContactsInfoViewHoc } from '../FormBody/FormBody';

export const Form: FC<
  IFormPopup & {
    shouldRestoreAdditionalData?: boolean;
  }
> = ({
  isLoading,
  isDialog = false,
  onClose,
  setHeader,
  isFormForceOpened,
  onFormSubmit,
  shouldRestoreAdditionalData,
  stepIndex,
  formId,
  submitButtonText = 'Далее',
  isAnyFormStepOpenedInPopup,
  onAuthorizationSuccess: onAuthorizationSuccessCallback,
  analyticsOverrides,
}) => {
  const dispatch = useAppDispatch();
  const [formData] = useUpdateStateBySelector(selectContactsInfoData);
  const authDefaultValues = useAppSelector(selectContactsInfoAuthData);
  const isAuth = useAppSelector(isLoggedSelector);

  useEffect(() => {
    sendEventShowStep(2, 'Контакты', analyticsOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContactsInfoData = async (actualData: TContactsFormFields, type: string) => {
    if (type === 'fullFilled') {
      dispatch(submitContactsForm(actualData, analyticsOverrides));
      onFormSubmit?.(actualData, isDialog);
    }
    if (isDialog) {
      onClose?.(true);
    }
  };

  const sendAnalyticsEvent = useCallback(
    ({ fieldName }: IAnalyticsEventFormField) => {
      const isParameter = isSatisfyOption(['userAgreement', 'advertisingAgreement'], fieldName);
      if (isParameter) {
        sendEventChangeFormFieldDefaultParam(
          stepIndex + 1,
          ContactsInfoTexts[fieldName as keyof TContactsFormFields],
          analyticsOverrides,
        );
      } else {
        sendEventFillFormField(
          stepIndex + 1,
          ContactsInfoTexts[fieldName as keyof TContactsFormFields],
          true,
          analyticsOverrides,
        );
      }
    },
    [stepIndex, analyticsOverrides],
  );

  const onAuthorizationSuccess = useCallback(
    (userInfo: FieldValues) => {
      dispatch(
        setContactsInfo({
          data: {
            mobilePhone: String(userInfo.phone || ''),
            userAgreement: userInfo.agreementPersonalData,
            advertisingAgreement: userInfo.agreementAdvertising,
          },
        }),
      );
      onAuthorizationSuccessCallback?.(userInfo);
    },
    [dispatch, onAuthorizationSuccessCallback],
  );

  if (!isAuth) {
    return (
      <SendEventWrapper sendEvent={sendAnalyticsEvent}>
        <AuthFormHoc
          authDefaultValues={{ ...authDefaultValues, agreementAdvertising: false, agreementPersonalData: true }}
          additionalProps={{
            isAnyFormStepOpenedInPopup,
            analyticsOverrides,
          }}
          formLabels={ContactsInfoTexts}
          formFieldsControls={FormFieldsControls}
          validationSchema={ContactsFormValidationSchema()}
          onAuthorizationSuccess={onAuthorizationSuccess as AuthFormProps['onAuthorizationSuccess']}
        />
      </SendEventWrapper>
    );
  }

  return (
    <SendEventWrapper sendEvent={sendAnalyticsEvent}>
      <ContactsInfoViewHoc
        isDialog={isDialog}
        onDataChanged={handleContactsInfoData}
        defaultData={formData}
        validationSchema={ContactsFormValidationSchema(true)}
        isLoading={isLoading}
        additionalProps={{
          setHeader,
          isFormForceOpened,
          shouldRestoreAdditionalData: !!shouldRestoreAdditionalData,
          formId,
          isAnyFormStepOpenedInPopup,
          analyticsOverrides,
        }}
        formLabels={ContactsInfoTexts}
        formFieldsControls={FormFieldsControls}
        submitButtonText={submitButtonText}
      />
    </SendEventWrapper>
  );
};
